import { Suspense, useEffect, useMemo } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { safeSetUser } from "@/utils/sentry";
import { StatsigProvider } from "@statsig/react-bindings";
import { dequal } from "dequal";
import CONFIG from "@/config";
import ErrorBoundary from "@/components/common/ErrorBoundary/ErrorBoundary";
import ProtectedRoute from "@components/auth/ProtectedRoute";
import Auth from "@components/auth/Auth";
import SideNav from "@/components/layout/SideNav/SideNav";
import Header from "@/components/layout/Header/Header";
import getRoutes from "@routes/routes";
import type { RouteConfig } from "@routes/routes.types";
import { type AppDispatch, type RootState } from "@/store/store";
import getUserDetail from "@/store/thunk/userDetail";
import { getDisabledFeaturesList } from "@/utils/featuresStatus";
import Home from "@/pages/Home/Home";
import NoMatch from "@/pages/NoMatch/NoMatch";
import ToasterComponent from "@/toaster/ToasterComponent";
import RefreshFilterAndSegmentOnMarketChange from "@/components/projectCommon/RefreshFilterAndSegmentOnMarketChange/RefreshFilterAndSegmentOnMarketChange";
import ProcessExportEvents from "@/components/FileDownload/ProcessExportEvents/ProcessExportEvents";
import { fetchIngestionTime } from "@/store/slices/ingestionTime/ingestionTIme.thunk";
import { useAbly } from "@/ably/AblyContext";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import ChatWindow from "@/components/AIAssistant/components/ChatWindow";
import ContractExpiryPopup from "@/components/projectCommon/ContractExpiryPopup/ContractExpiryPopup";
import ValidateView from "@/components/projectCommon/ValidateView/ValidateView";
import tokenManager from "@/utils/tokenManager";
import { REDIRECT_URL } from "@/constant/constantsThrougoutProject";
import { buildUrl } from "@/utils/url";

function App() {
  const dispatch = useDispatch<AppDispatch>();
  const ablyClient = useAbly();
  const statsigTier = (() => {
    const rawTier = CONFIG.env?.toLowerCase() ?? "development";
    if (rawTier === "dev") return "development";
    if (rawTier === "prod" || rawTier === "production") return "production";
    if (rawTier === "stage") return "staging";
    return rawTier;
  })();
  const user = useSelector(
    (state: RootState) => state.userDetail || null,
    (prev, next) => {
      return dequal(prev, next);
    }
  );

  useEffect(() => {
    if (user) {
      safeSetUser({
        id: String(user.id),
        email: user.email,
        name: user.name,
      });
    }
  }, [user]);

  const featureStatus = useSelector(
    (state: RootState) => state.featureStatus || null,
    (prev, next) => {
      return dequal(prev, next);
    }
  );

  const routes: RouteConfig[] = useMemo(() => {
    const hiddenRoutes = getDisabledFeaturesList(featureStatus);
    return getRoutes(hiddenRoutes);
  }, [featureStatus]);

  const isChatOpen = useSelector(
    (state: RootState) => state.chat?.isChatOpen || false
  );

  const marketIds = useSelector(
    (state: RootState) => state.activeGroup.markets.markets.ids,
    (prev, next) => dequal(prev, next)
  );

  const { isContractExpired, isDataFetched } = useSelector(
    (state: RootState) => ({
      isContractExpired:
        state.activeGroup.bootstrapDataStatus.isContractExpired,
      isDataFetched: state.activeGroup.bootstrapDataStatus.isDataFetched,
    }),
    (prev, next) => dequal(prev, next)
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tokenFromUrl = params.get("t");
    const token = tokenManager.getToken();
    if (!token && !tokenFromUrl) {
      const params = {
        domain: encodeURIComponent(window.location.origin),
        completeUrl: CONFIG.endPoints.auth0 + "/",
        failUrl: CONFIG.endPoints.auth0 + "/register/fail",
        serverLogin: "true",
      };
      sessionStorage.setItem(REDIRECT_URL, window.location.href);
      const loginURL = buildUrl(
        CONFIG.endPoints.userService,
        "/auth/login",
        params
      );
      window.location.href = loginURL;
      return
    }
    if (token && token?.length > 0) {
      dispatch(getUserDetail());
    }
  }, [dispatch]);

  useEffect(() => {
    if (marketIds?.length > 0) {
      dispatch(fetchIngestionTime({ markets: marketIds }));
    }
  }, [marketIds]);

  return (
    <>
      <ProcessExportEvents
        ablyClient={ablyClient}
        clientId={(user && String(user.id)) || ""}
      />

      <Router>
        <RefreshFilterAndSegmentOnMarketChange />
        <ValidateView />
        <Auth>
          {isContractExpired ? (
            <ContractExpiryPopup />
          ) : isDataFetched ? (
            <StatsigProvider
              sdkKey={CONFIG.statSig.statSigSDKKey}
              user={{
                email: user?.email?.toLowerCase(),
                userID: user?.email?.toLowerCase(),
              }}
              options={{
                environment: {
                  tier: statsigTier
                },
              }}
            >
              <div className="flex h-screen overflow-hidden">
                <SideNav routes={routes} />
                <main
                  className="flex-1 h-screen text-text-500 bg-[#f5f7fa]"
                  style={{
                    width: isChatOpen
                      ? "calc(100% - 280px - 600px)"
                      : "calc(100% - 280px)",
                    transition: "width 0.3s ease",
                  }}
                >
                  <Header routes={routes} />
                  <ErrorBoundary>
                    <Suspense
                      fallback={
                        <div className="p-3 bg-white flex items-center justify-center h-full w-full">
                          <img
                            className="w-[220px] mb-[72px]"
                            src="/images/logo/harris_questbrand_complete_logo.png"
                            alt="Harris poll"
                          />
                        </div>
                      }
                    >
                      <Routes>
                        <Route
                          key={"home"}
                          path={"/"}
                          element={
                            <ProtectedRoute>
                              <Home />
                            </ProtectedRoute>
                          }
                        />
                        {routes.map((route) => {
                          const Element = route.component;
                          return (
                            <Route
                              key={route.id}
                              path={route.path}
                              element={
                                <ProtectedRoute>
                                  <Element />
                                </ProtectedRoute>
                              }
                            />
                          );
                        })}
                        {routes.length > 0 && routes[0].redirectTo && (
                          <Route
                            index
                            element={
                              <ProtectedRoute>
                                <Home />
                              </ProtectedRoute>
                            }
                          />
                        )}
                        <Route
                          path="*"
                          element={
                            <ProtectedRoute>
                              <NoMatch />
                            </ProtectedRoute>
                          }
                        />
                      </Routes>
                    </Suspense>
                  </ErrorBoundary>
                </main>
                {/* Chat window docked to the right of main */}
                <ChatWindow />
              </div>
            </StatsigProvider>
          ) : (
            <div className="flex h-lvh justify-center items-center">
              <img
                className="w-[260px]"
                src="/images/logo/harris_questbrand_complete_logo.png"
                alt="Harris poll"
              />
            </div>
          )}
        </Auth>
      </Router>
      <ToasterComponent />
    </>
  );
}

export default App;
